/**
 * POS 기기 상세 페이지
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner, Row, Col, Alert } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { posService } from '../../services/posService';
import { PosDevice } from '../../types/pos';
import { format } from 'date-fns';

const PosDeviceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deviceId = id ? parseInt(id, 10) : 0;
  const isNew = id === 'new';

  const { data: device, isLoading } = useQuery({
    queryKey: ['posDevice', deviceId],
    queryFn: () => posService.getDevice(deviceId),
    enabled: !!deviceId && deviceId > 0 && !isNew,
  });

  const deleteMutation = useMutation({
    mutationFn: () => posService.deleteDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posDevices'] });
      navigate('/admin/pos/devices');
    },
    onError: (error: any) => {
      alert(`삭제 실패: ${error.message}`);
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">활성</Badge>;
      case 'inactive':
        return <Badge bg="secondary">비활성</Badge>;
      case 'maintenance':
        return <Badge bg="warning">점검중</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const handleDelete = () => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      deleteMutation.mutate();
    }
  };

  if (isNew) {
    navigate('/admin/pos/devices/new');
    return null;
  }

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!device) {
    return (
      <AdminLayout>
        <Card>
          <Card.Body>
            <p>POS 기기를 찾을 수 없습니다.</p>
            <Button variant="secondary" onClick={() => navigate('/admin/pos/devices')}>
              목록으로
            </Button>
          </Card.Body>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>POS 기기 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/pos/devices')} className="me-2">
              목록으로
            </Button>
            <Button variant="info" onClick={() => navigate(`/admin/pos/sales?device_id=${deviceId}`)} className="me-2">
              매출 조회
            </Button>
            <Button variant="primary" onClick={() => navigate(`/admin/pos/devices/${deviceId}/edit`)} className="me-2">
              수정
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              삭제
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <h3>{device.device_name}</h3>
                  <div className="mt-2">
                    {getStatusBadge(device.status)}
                  </div>
                </div>

                <div className="mb-3">
                  <strong>기기 코드:</strong> 
                  <span style={{ color: '#ccc', marginLeft: '10px' }}>{device.device_code}</span>
                </div>

                {device.location && (
                  <div className="mb-3">
                    <strong>설치 위치:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>{device.location}</span>
                  </div>
                )}

                {device.device_type && (
                  <div className="mb-3">
                    <strong>기기 유형:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>{device.device_type}</span>
                  </div>
                )}
              </Col>

              <Col md={6}>
                {device.last_sync_at && (
                  <div className="mb-3">
                    <strong>최종 동기화:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>
                      {format(new Date(device.last_sync_at), 'yyyy-MM-dd HH:mm:ss')}
                    </span>
                  </div>
                )}

                <div className="mb-3">
                  <strong>등록일:</strong> 
                  <span style={{ color: '#ccc', marginLeft: '10px' }}>
                    {format(new Date(device.created_at), 'yyyy-MM-dd HH:mm:ss')}
                  </span>
                </div>

                {device.updated_at && (
                  <div className="mb-3">
                    <strong>수정일:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>
                      {format(new Date(device.updated_at), 'yyyy-MM-dd HH:mm:ss')}
                    </span>
                  </div>
                )}

                {device.connection_info && (
                  <div className="mb-3">
                    <strong>연동 정보:</strong> 
                    <pre style={{ 
                      color: '#ccc', 
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#2E2E3A',
                      borderRadius: '5px',
                      fontSize: '12px'
                    }}>
                      {JSON.stringify(device.connection_info, null, 2)}
                    </pre>
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>매출 현황</h5>
              <Button variant="info" size="sm" onClick={() => navigate(`/admin/pos/sales?device_id=${deviceId}`)}>
                매출 상세 보기
              </Button>
            </div>
            <Alert variant="info">
              POS 기기별 매출 데이터는 실시간으로 동기화되거나 수동으로 입력할 수 있습니다.
            </Alert>
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PosDeviceDetail;

